import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Plus from 'lucide-react/dist/esm/icons/plus.mjs'
import LogOut from 'lucide-react/dist/esm/icons/log-out.mjs'
import Target from 'lucide-react/dist/esm/icons/target.mjs'
import { useProjectData } from '../store/AppStore'
import { ModalShell } from '../shared/ui/ModalShell'
import { ToastViewport } from '../shared/ui/ToastViewport'
import { createId } from '../shared/utils/id'
import { canAccessPermission, getMemberProjectIds } from '../shared/utils/permissions'
import { flatNavigation, platformNavigationItems } from './navigation'
import { GlobalSearchDialog } from './GlobalSearchDialog'
import { QuickActionHost } from './QuickActionHost'

const projectAccents = ['#6653d7', '#3b8f68', '#d78335', '#4b82b7', '#b35f8c']

function ProjectDialog({ state, currentMember, upsert, selectProject, navigate, notify, close }) {
  const [form, setForm] = useState({ name: '', accent: projectAccents[state.projects.length % projectAccents.length] })
  const save = () => {
    const id = createId('project')
    upsert('projects', {
      id,
      code: `项目 ${state.projects.length + 1}`,
      name: form.name.trim(),
      accent: form.accent,
      demoDate: new Date().toISOString().slice(0, 10),
      storageLimitGb: 100,
      notificationLeadMinutes: 15,
      integrations: { wecom: false, douyin: false, xiaohongshu: false, wechat: false },
    })
    if (currentMember.role !== 'owner') upsert('members', { ...currentMember, projectIds: [...new Set([...(currentMember.projectIds || []), id])] })
    selectProject(id)
    navigate('/overview')
    notify(`项目“${form.name.trim()}”已创建`)
    close()
  }
  return <ModalShell eyebrow="NEW PROJECT" title="创建项目" close={close} className="project-create-modal" footer={<><button className="btn secondary" onClick={close}>取消</button><button className="btn primary" disabled={!form.name.trim()} onClick={save}>创建项目</button></>}><div className="modal-content"><div className="form-stack"><label><span>项目名称</span><input autoFocus value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))}/></label><label><span>项目颜色</span><div className="project-accent-picker">{projectAccents.map(accent => <button type="button" className={form.accent === accent ? 'selected' : ''} style={{ background: accent }} aria-label={`选择项目颜色 ${accent}`} key={accent} onClick={() => setForm(current => ({ ...current, accent }))}/>)}</div></label></div></div></ModalShell>
}

export function AppShell() {
  const { state, project, filter, toasts, selectProject, upsert, notify, logout } = useProjectData()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  const [projectDialogOpen, setProjectDialogOpen] = useState(false)
  const currentMember = state.members.find(member => member.id === state.currentMemberId) || state.members.find(member => member.role === 'owner') || state.members[0]
  const accessibleProjectIds = getMemberProjectIds(currentMember, state.projects)
  const accessibleProjects = state.projects.filter(item => accessibleProjectIds.includes(item.id))
  const visibleNavigation = flatNavigation.filter(item => canAccessPermission(currentMember, project?.id, item.permission))
  const platformNavigation = platformNavigationItems.filter(item => !item.ownerOnly || currentMember?.role === 'owner')
  const isPlatformRoute = platformNavigationItems.some(item => location.pathname.startsWith(item.path))
  const canAccessCurrentRoute = platformNavigation.some(item => location.pathname.startsWith(item.path)) || visibleNavigation.some(item => location.pathname.startsWith(item.path))

  useEffect(() => {
    const handleKeyDown = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearchOpen(true)
      }
      if (event.key === 'Escape') {
        setSearchOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (project && visibleNavigation.length && !canAccessCurrentRoute) navigate(visibleNavigation[0].path, { replace: true })
  }, [canAccessCurrentRoute, isPlatformRoute, navigate, project, visibleNavigation])

  const collectionCounts = useMemo(() => ({ plans: filter('plans').length, assets: filter('assets').length }), [filter])

  const switchProject = nextProject => {
    const target = flatNavigation.find(item => canAccessPermission(currentMember, nextProject.id, item.permission))
    selectProject(nextProject.id)
    navigate(target?.path || '/overview')
    notify(`已切换到${nextProject.name}`)
  }

  return <div className="app workspace-layout">
    <aside className="project-rail">
      <div className="project-rail-logo"><Target size={20}/></div>
      {platformNavigation.length ? <div className="project-rail-platform">{platformNavigation.map(item => { const Icon = item.icon; const active = location.pathname.startsWith(item.path); return <button type="button" className={active ? 'active' : ''} aria-label={item.label} title={item.label} key={item.path} onClick={() => navigate(item.path)}><Icon size={18}/><span>成员权限</span></button> })}</div> : null}
      <div className="project-rail-list">{accessibleProjects.map(item => <button type="button" className={item.id === project?.id ? 'active' : ''} title={item.name} aria-label={`切换到${item.name}`} key={item.id} onClick={() => switchProject(item)}><i style={{ background: item.accent }}>{Array.from(item.name.trim())[0]?.toUpperCase() || '项'}</i><span>{item.name}</span></button>)}</div>
      {currentMember?.role === 'owner' ? <button className="project-rail-add" type="button" aria-label="创建项目" title="创建项目" onClick={() => setProjectDialogOpen(true)}><Plus size={16}/></button> : null}
      <button type="button" className="project-rail-user" title={`${currentMember?.name || '当前成员'} · 退出登录`} aria-label="退出登录" onClick={logout}><span>{currentMember?.initials || currentMember?.name?.slice(0, 2).toUpperCase()}</span><LogOut size={11}/></button>
    </aside>
    <aside className="sidebar workspace-menu">
      <nav>{visibleNavigation.map(item => { const Icon = item.icon; const active = location.pathname.startsWith(item.path); return <button key={item.path} className={active ? 'active' : ''} onClick={() => navigate(item.path)}><Icon size={18}/><span>{item.label}</span>{item.collection ? <em>{collectionCounts[item.collection]}</em> : null}</button> })}</nav>
    </aside>
    <main>
      <div className="page-content">{canAccessCurrentRoute ? <Outlet/> : <div className="card access-denied"><Target size={28}/><h2>{isPlatformRoute ? '仅超级管理员可以访问此功能' : '当前项目没有可访问功能'}</h2></div>}</div>
    </main>
    {searchOpen ? <GlobalSearchDialog close={() => setSearchOpen(false)} allowedPermissions={new Set(visibleNavigation.map(item => item.permission))}/> : null}
    {projectDialogOpen ? <ProjectDialog state={state} currentMember={currentMember} upsert={upsert} selectProject={selectProject} navigate={navigate} notify={notify} close={() => setProjectDialogOpen(false)}/> : null}
    <QuickActionHost/>
    <ToastViewport toasts={toasts}/>
  </div>
}
